var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isNullOrUndefined, Complex } from '@syncfusion/ej2-base';
import { Property, ChildProperty } from '@syncfusion/ej2-base';
import { DataUtil } from '@syncfusion/ej2-data';
import { increaseDateTimeInterval } from '../utils/helper';
import { BubbleData } from '../model/base';
/**
 * Configures the adaptor in the heatmap.
 */
export class Data extends ChildProperty {
}
__decorate([
    Property(false)
], Data.prototype, "isJsonData", void 0);
__decorate([
    Property('None')
], Data.prototype, "adaptorType", void 0);
__decorate([
    Property('')
], Data.prototype, "xDataMapping", void 0);
__decorate([
    Property('')
], Data.prototype, "yDataMapping", void 0);
__decorate([
    Property('')
], Data.prototype, "valueMapping", void 0);
__decorate([
    Complex({}, BubbleData)
], Data.prototype, "bubbleDataMapping", void 0);
export class AdaptiveMinMax {
}
/**
 *
 * The `Adaptor` module is used to handle JSON and Table data.
 */
export class Adaptor {
    constructor(heatMap) {
        this.reconstructedXAxis = [];
        this.reconstructedYAxis = [];
        this.adaptiveXMinMax = new AdaptiveMinMax();
        this.adaptiveYMinMax = new AdaptiveMinMax();
        this.heatMap = heatMap;
    }
    /**
     * Method to construct Two Dimentional Datasource.
     *
     * @returns {void}
     * @private
     */
    constructDatasource(dataSource, dataSourceSettings) {
        if (dataSourceSettings.adaptorType === 'Cell') {
            const xAxis = this.heatMap.xAxis;
            const yAxis = this.heatMap.yAxis;
            this.adaptiveXMinMax.min = xAxis.minimum;
            this.adaptiveXMinMax.max = xAxis.maximum;
            this.adaptiveYMinMax.min = yAxis.minimum;
            this.adaptiveYMinMax.max = yAxis.maximum;
            if (((xAxis.valueType === 'Numeric' || xAxis.valueType === 'DateTime') &&
                (isNullOrUndefined(xAxis.minimum) || isNullOrUndefined(xAxis.maximum))) ||
                ((yAxis.valueType === 'Numeric' || yAxis.valueType === 'DateTime') &&
                    (isNullOrUndefined(yAxis.minimum) || isNullOrUndefined(yAxis.maximum)))) {
                this.getMinMaxValue(dataSource, dataSourceSettings, xAxis, yAxis);
            }
            this.heatMap.isCellData = true;
        }
        if (dataSourceSettings.adaptorType === 'None') {
            this.heatMap.completeAdaptDataSource = dataSource;
        }
        else if (!dataSourceSettings.isJsonData && dataSourceSettings.adaptorType === 'Table') {
            this.heatMap.completeAdaptDataSource = dataSource;
        }
        else if (dataSourceSettings.isJsonData && dataSourceSettings.adaptorType === 'Table') {
            this.heatMap.completeAdaptDataSource = this.processJsonTableData(dataSource, dataSourceSettings);
        }
        else if (dataSourceSettings.isJsonData && dataSourceSettings.adaptorType === 'Cell') {
            this.heatMap.completeAdaptDataSource = this.processJsonCellData(dataSource, dataSourceSettings);
        }
        else if (!dataSourceSettings.isJsonData && dataSourceSettings.adaptorType === 'Cell') {
            this.constructAdaptiveAxis();
            this.heatMap.completeAdaptDataSource = this.processCellData(dataSource);
            this.heatMap.isCellData = true;
        }
    }
    /**
     * Method to construct Axis Collection.
     *
     * @returns {void}
     * @private
     */
    constructAdaptiveAxis() {
        const xAxis = this.heatMap.xAxis;
        const yAxis = this.heatMap.yAxis;
        if (xAxis.valueType === 'Numeric') {
            this.reconstructedXAxis = this.getNumericAxisCollection(this.adaptiveXMinMax.min, this.adaptiveXMinMax.max, xAxis.increment);
        }
        if (yAxis.valueType === 'Numeric') {
            this.reconstructedYAxis = this.getNumericAxisCollection(this.adaptiveYMinMax.min, this.adaptiveYMinMax.max, yAxis.increment);
        }
        if (xAxis.valueType === 'DateTime') {
            this.reconstructedXAxis = this.getDateAxisCollection(this.adaptiveXMinMax.min, this.adaptiveXMinMax.max, xAxis.intervalType, xAxis.increment);
        }
        if (yAxis.valueType === 'DateTime') {
            this.reconstructedYAxis = this.getDateAxisCollection(this.adaptiveYMinMax.min, this.adaptiveYMinMax.max, yAxis.intervalType, yAxis.increment);
        }
    }
    /**
     * Method to calculate Numeric Axis Collection.
     *
     * @returns {string[]}
     * @private
     */
    getNumericAxisCollection(min, max, increment) {
        let loopIndex = min;
        const tempAxisColl = [];
        while (loopIndex <= max) {
            tempAxisColl.push(loopIndex.toString());
            loopIndex = loopIndex + increment;
        }
        return tempAxisColl;
    }
    /**
     * Method to calculate DateTime Axis Collection.
     *
     * @returns {string[]}
     * @private
     */
    getDateAxisCollection(min, max, intervalType, increment) {
        const option = {
            skeleton: 'full',
            type: 'dateTime'
        };
        const dateParser = this.heatMap.intl.getDateParser(option);
        const dateFormatter = this.heatMap.intl.getDateFormat(option);
        min = Date.parse(dateParser(dateFormatter(new Date(DataUtil.parse.parseJson({ val: min }).val))));
        let tempInterval = min;
        const tempAxisColl = [];
        while (tempInterval <= max) {
            tempAxisColl.push(new Date(tempInterval).toString());
            tempInterval = increaseDateTimeInterval(tempInterval, 1, intervalType, increment).getTime();
        }
        return tempAxisColl;
    }
    /**
     * Method to calculate Maximum and Minimum Value from datasource.
     *
     * @returns {void}
     * @private
     */
    getMinMaxValue(dataSource, adapData, xAxis, yAxis) {
        const data = dataSource;
        const label = Object.keys(data[0]);
        if (data.length > 0) {
            this.adaptiveXMinMax.min = !isNullOrUndefined(xAxis.minimum) ? xAxis.minimum : adapData.isJsonData ?
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data[0][label[0]] : data[0][0];
            this.adaptiveYMinMax.min = !isNullOrUndefined(yAxis.minimum) ? yAxis.minimum : adapData.isJsonData ?
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data[0][label[1]] : data[0][1];
            this.adaptiveXMinMax.max = !isNullOrUndefined(xAxis.maximum) ? xAxis.maximum : adapData.isJsonData ?
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data[0][label[0]] : data[0][0];
            this.adaptiveYMinMax.max = !isNullOrUndefined(yAxis.maximum) ? yAxis.maximum : adapData.isJsonData ?
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data[0][label[1]] : data[0][1];
        }
        for (let dataIndex = 0; dataIndex < data.length; dataIndex++) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const xDataIndex = adapData.isJsonData ? data[dataIndex][label[0]] : data[dataIndex][0];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const yDataIndex = adapData.isJsonData ? data[dataIndex][label[1]] : data[dataIndex][1];
            if (xDataIndex < this.adaptiveXMinMax.min && isNullOrUndefined(xAxis.minimum)) {
                this.adaptiveXMinMax.min = xDataIndex;
            }
            if (xDataIndex > this.adaptiveXMinMax.max && isNullOrUndefined(xAxis.maximum)) {
                this.adaptiveXMinMax.max = xDataIndex;
            }
            if (yDataIndex < this.adaptiveYMinMax.min && isNullOrUndefined(yAxis.minimum)) {
                this.adaptiveYMinMax.min = yDataIndex;
            }
            if (yDataIndex > this.adaptiveYMinMax.max && isNullOrUndefined(yAxis.maximum)) {
                this.adaptiveYMinMax.max = yDataIndex;
            }
        }
    }
    /**
     * Method to process Cell datasource.
     *
     * @returns {Object}
     * @private
     */
    processCellData(dataSource) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tempDataCollection = dataSource;
        const xLabels = this.reconstructedXAxis;
        const yLabels = this.reconstructedYAxis;
        let currentDataXIndex = 0;
        let currentDataYIndex = 0;
        this.reconstructData = [];
        if (tempDataCollection && tempDataCollection.length) {
            for (let xindex = 0; xindex < tempDataCollection.length; xindex++) {
                if (this.heatMap.xAxis.valueType === 'Category') {
                    currentDataXIndex = tempDataCollection[xindex][0];
                }
                else {
                    currentDataXIndex = xLabels.indexOf(tempDataCollection[xindex][0].toString());
                }
                if (currentDataXIndex > -1) {
                    while (!this.reconstructData[currentDataXIndex]) {
                        this.reconstructData.push([]);
                    }
                    if (this.heatMap.yAxis.valueType === 'Category') {
                        currentDataYIndex = tempDataCollection[xindex][1];
                    }
                    else {
                        currentDataYIndex = yLabels.indexOf(tempDataCollection[xindex][1].toString());
                    }
                    if (currentDataYIndex !== -1) {
                        while (this.reconstructData[currentDataXIndex][currentDataYIndex] !== '') {
                            this.reconstructData[currentDataXIndex].push('');
                        }
                        this.reconstructData[currentDataXIndex][currentDataYIndex] =
                            isNullOrUndefined(tempDataCollection[xindex][2]) ?
                                '' : tempDataCollection[xindex][2];
                    }
                }
            }
        }
        return this.reconstructData;
    }
    /**
     * Method to process JSON Cell datasource.
     *
     * @returns {Object}
     * @private
     */
    processJsonCellData(dataSource, adaptordata) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tempDataCollection = dataSource;
        const xAxisLabels = this.heatMap.xAxis.labels ? this.heatMap.xAxis.labels : [];
        const yAxisLabels = this.heatMap.yAxis.labels ? this.heatMap.yAxis.labels : [];
        const axisCollections = this.heatMap.axisCollections;
        if (xAxisLabels.length === 0 || yAxisLabels.length === 0) {
            this.generateAxisLabels(dataSource, adaptordata);
        }
        const xLabels = (this.heatMap.xAxis.valueType === 'Category') ? (xAxisLabels.length > 0 ?
            this.heatMap.xAxis.labels : axisCollections[0].jsonCellLabel) : axisCollections[0].labelValue;
        const yLabels = (this.heatMap.yAxis.valueType === 'Category') ? (yAxisLabels.length > 0 ?
            this.heatMap.yAxis.labels : axisCollections[1].jsonCellLabel) : axisCollections[1].labelValue;
        let currentDataXIndex = 0;
        let currentDataYIndex = 0;
        if (tempDataCollection.length) {
            this.reconstructData = [];
            for (let index = 0; index < tempDataCollection.length; index++) {
                currentDataXIndex = this.getSplitDataValue(tempDataCollection[index], adaptordata, xLabels, !isNullOrUndefined(adaptordata.xDataMapping) ?
                    adaptordata.xDataMapping.split('.') : null, this.heatMap.xAxis.valueType);
                if (currentDataXIndex !== -1) {
                    while (!this.reconstructData[currentDataXIndex]) {
                        this.reconstructData.push([]);
                    }
                    currentDataYIndex = this.getSplitDataValue(tempDataCollection[index], adaptordata, yLabels, !isNullOrUndefined(adaptordata.yDataMapping) ?
                        adaptordata.yDataMapping.split('.') : null, this.heatMap.yAxis.valueType);
                    if (currentDataYIndex !== -1) {
                        while (isNullOrUndefined(this.reconstructData[currentDataXIndex][currentDataYIndex])) {
                            this.reconstructData[currentDataXIndex].push('');
                        }
                        if (this.heatMap.bubbleSizeWithColor) {
                            this.reconstructData[currentDataXIndex][currentDataYIndex] = [
                                this.getSplitDataValue(tempDataCollection[index], adaptordata, null, adaptordata.bubbleDataMapping.size.split('.'), ''),
                                this.getSplitDataValue(tempDataCollection[index], adaptordata, null, adaptordata.bubbleDataMapping.color.split('.'), '')
                            ];
                        }
                        else {
                            this.reconstructData[currentDataXIndex][currentDataYIndex] = this.getSplitDataValue(tempDataCollection[index], adaptordata, null, !isNullOrUndefined(adaptordata.valueMapping) ?
                                adaptordata.valueMapping.split('.') : null, '');
                        }
                    }
                }
            }
        }
        return this.reconstructData;
    }
    /**
     * Method to generate axis labels when labels are not given.
     *
     * @returns {string}
     * @private
     */
    generateAxisLabels(dataSource, adaptordata) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tempDataCollection = dataSource;
        const xLabels = this.heatMap.xAxis.labels ? this.heatMap.xAxis.labels : [];
        const yLabels = this.heatMap.yAxis.labels ? this.heatMap.yAxis.labels : [];
        const hasXLabels = xLabels.length > 0 ? true : false;
        const hasYLabels = yLabels.length > 0 ? true : false;
        const axisCollection = this.heatMap.axisCollections;
        for (let index = 0; index < axisCollection.length; index++) {
            const valueType = axisCollection[index].valueType;
            const axis = axisCollection[index];
            if (valueType === 'Category') {
                let hasLabels;
                let dataMapping;
                let labels;
                if (axis.orientation === 'Horizontal') {
                    hasLabels = hasXLabels;
                    dataMapping = adaptordata.xDataMapping;
                    axis.jsonCellLabel = labels = [];
                }
                else {
                    hasLabels = hasYLabels;
                    dataMapping = adaptordata.yDataMapping;
                    axis.jsonCellLabel = labels = [];
                }
                if (!hasLabels) {
                    for (let i = 0; i < tempDataCollection.length; i++) {
                        if (dataMapping in tempDataCollection[i]) {
                            const xValue = tempDataCollection[i][dataMapping].toString();
                            if (labels.indexOf(xValue.toString()) === -1) {
                                labels.push(xValue);
                            }
                        }
                    }
                }
            }
            else if (valueType === 'DateTime') {
                axis.clearAxisLabel();
                axis.calculateDateTimeAxisLabel(this.heatMap);
            }
            else {
                axis.clearAxisLabel();
                axis.calculateNumericAxisLabels(this.heatMap);
            }
        }
    }
    /**
     * Method to get data from complex mapping.
     *
     * @returns {number|string}
     * @private
     */
    getSplitDataValue(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tempSplitDataCollection, adaptordata, labels, tempSplitData, valueType) {
        let value = -1;
        const option = {
            skeleton: 'full',
            type: 'dateTime'
        };
        const dateParser = this.heatMap.intl.getDateParser(option);
        const dateFormatter = this.heatMap.intl.getDateFormat(option);
        this.tempSplitDataCollection = tempSplitDataCollection;
        for (let splitIndex = 0; splitIndex < (!isNullOrUndefined(tempSplitData) ? tempSplitData.length : 0); splitIndex++) {
            value = !isNullOrUndefined(labels) ? (!(valueType === 'DateTime') ?
                labels.indexOf(this.tempSplitDataCollection[tempSplitData[splitIndex]]) :
                ((typeof this.tempSplitDataCollection[tempSplitData[splitIndex]] === 'string' ||
                    typeof this.tempSplitDataCollection[tempSplitData[splitIndex]] === 'object') ?
                    labels.map(Number).indexOf(Date.parse(dateParser(dateFormatter(new Date(DataUtil.parse.parseJson({ val: this.tempSplitDataCollection[tempSplitData[splitIndex]] }).val))))) :
                    // eslint-disable-next-line max-len
                    labels.map(Number).indexOf(+new Date(this.tempSplitDataCollection[tempSplitData[splitIndex]]).setHours(0, 0, 0, 0)))) : null;
            if (!isNullOrUndefined(this.tempSplitDataCollection)) {
                this.tempSplitDataCollection = value !== -1 && !isNullOrUndefined(labels) ?
                    this.tempSplitDataCollection : this.tempSplitDataCollection[tempSplitData[splitIndex]];
            }
            if (isNullOrUndefined(this.tempSplitDataCollection)) {
                break;
            }
        }
        value = !isNullOrUndefined(labels) ? value : isNullOrUndefined(this.tempSplitDataCollection) ||
            this.tempSplitDataCollection.toString() === '' ? '' : parseFloat(this.tempSplitDataCollection.toString());
        return value;
    }
    /**
     * Method to process JSON Table datasource.
     *
     * @returns {Object}
     * @private
     */
    processJsonTableData(dataSource, adaptordata) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tempDataCollection = dataSource;
        let currentDataXIndex = 0;
        let currentDataYIndex = 0;
        const xLabels = this.heatMap.xAxis.labels ? this.heatMap.xAxis.labels : [];
        const yLabels = this.heatMap.yAxis.labels ? this.heatMap.yAxis.labels : [];
        let key;
        if (tempDataCollection.length) {
            this.reconstructData = [];
            for (let xindex = 0; xindex < tempDataCollection.length; xindex++) {
                currentDataXIndex = this.getSplitDataValue(tempDataCollection[xindex], adaptordata, xLabels, !isNullOrUndefined(adaptordata.xDataMapping) ?
                    adaptordata.xDataMapping.split('.') : null, this.heatMap.xAxis.valueType);
                if (currentDataXIndex !== -1) {
                    while (!this.reconstructData[currentDataXIndex]) {
                        this.reconstructData.push([]);
                    }
                    for (let index = 0; index < Object.keys(this.tempSplitDataCollection).length; index++) {
                        key = Object.keys(this.tempSplitDataCollection)[index];
                        currentDataYIndex = key !== adaptordata.xDataMapping ? yLabels.indexOf(key) : -1;
                        if (currentDataYIndex !== -1) {
                            while (isNullOrUndefined(this.reconstructData[currentDataXIndex][currentDataYIndex])) {
                                this.reconstructData[currentDataXIndex].push('');
                            }
                            this.reconstructData[currentDataXIndex][currentDataYIndex] =
                                isNullOrUndefined(this.tempSplitDataCollection[key]) ?
                                    '' : this.tempSplitDataCollection[key];
                        }
                    }
                }
            }
        }
        return this.reconstructData;
    }
    /**
     * To destroy the Adaptor.
     *
     * @returns {void}
     * @private
     */
    destroy() {
        this.reconstructData = null;
        this.reconstructedXAxis = null;
        this.reconstructedYAxis = null;
        this.adaptiveXMinMax = null;
        this.adaptiveYMinMax = null;
        this.tempSplitDataCollection = null;
        this.heatMap = null;
    }
    /**
     * To get Module name
     */
    getModuleName() {
        return 'Adaptor';
    }
}
